import React, { useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import {
  Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, InputAdornment, MenuItem, Radio, RadioGroup, Switch, TextField, Typography,
} from '@mui/material'
import { T } from '../PartnerSignups/onboardingDesign'
import { FieldLabel, GhostButton, Notice, PrimaryButton } from '../PartnerSignups/onboardingUi'
import { BankFields, RatesEditor, bankToForm, formToRates, ratesToForm } from './isoParts'

/**
 * Super admin: a new ISO, or an ISO's details (resolvers/iso.js createIso /
 * updateIso). The tax ID and bank numbers come back masked to their last 4;
 * leaving them as they are keeps what is stored.
 */

const CREATE = gql`mutation CreateIso($input: IsoInput!) { createIso(input: $input) { _id code } }`
const UPDATE = gql`mutation UpdateIso($id: ID!, $input: IsoInput!) { updateIso(id: $id, input: $input) { _id isActive payoutTo } }`
const CONVERT = gql`mutation ConvertAgentToIso($vendorId: ID!, $input: IsoInput!) { convertAgentToIso(vendorId: $vendorId, input: $input) { _id code } }`

const section = { fontWeight: 700, fontSize: 15.5, color: T.text, mt: 3, mb: 0.5 }
const sectionSub = { color: T.hint, fontSize: 13.5, mb: 1.75 }

// convertFrom: one of RushMyFood's agents becoming an ISO (backend
// utils/isos.js convertAgent), their name, email and phone filled in.
export default function IsoDialog({ iso, convertFrom = null, onClose, onSaved }) {
  const editing = !!iso
  const from = !editing ? convertFrom : null
  const [f, setF] = useState(() => ({
    corporateName: iso ? iso.corporateName || '' : from ? from.name || '' : '',
    email: iso ? iso.email || '' : from ? from.email || '' : '',
    phone: iso ? iso.phone || '' : from ? from.phone || '' : '',
    address1: iso ? iso.address1 || '' : '',
    address2: iso ? iso.address2 || '' : '',
    city: iso ? iso.city || '' : '',
    state: iso ? iso.state || '' : '',
    zip: iso ? iso.zip || '' : '',
    taxIdType: iso ? iso.taxIdType || 'EIN' : 'EIN',
    taxId: iso ? iso.taxId || '' : '',
    payoutTo: iso ? iso.payoutTo || 'ISO' : 'ISO',
    // Its share of an account's fees above the minimums; 50% by default.
    extraShare: iso ? String(iso.extraShare ?? '') : '50',
    isActive: iso ? iso.isActive : true,
  }))
  const [bank, setBank] = useState(() => bankToForm(iso && iso.bank))
  const [rates, setRates] = useState(() => ratesToForm(iso && iso.rates))
  const [error, setError] = useState(null)
  const [create, { loading: creating }] = useMutation(CREATE)
  const [update, { loading: updating }] = useMutation(UPDATE)
  const [convert, { loading: converting }] = useMutation(CONVERT)
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }))
  const busy = creating || updating || converting

  const save = async () => {
    setError(null)
    const input = {
      corporateName: f.corporateName,
      phone: f.phone || null,
      address1: f.address1,
      address2: f.address2,
      city: f.city,
      state: f.state,
      zip: f.zip,
      taxIdType: f.taxIdType,
      taxId: f.taxId,
      bank,
      rates: formToRates(rates),
      payoutTo: f.payoutTo,
      extraShare: f.extraShare === '' ? null : Number(f.extraShare),
    }
    try {
      if (editing) {
        await update({ variables: { id: iso._id, input: { ...input, isActive: f.isActive } } })
      } else {
        if (from) await convert({ variables: { vendorId: from._id, input } })
        else await create({ variables: { input: { ...input, email: f.email } } })
      }
      onSaved()
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? `Edit ${iso.corporateName}` : from ? `Convert ${from.name} to an ISO` : 'New ISO'}</DialogTitle>
      <DialogContent dividers sx={{ borderColor: T.divider }}>
        {error && <Notice severity="error">{error}</Notice>}
        {from && (
          <Notice severity="info">
            {from.name} keeps their login, sign-up link and history. Their portal becomes an ISO portal, where they add their own agents.
            {' '}{from.restaurants
              ? `Their ${from.restaurants === 1 ? 'restaurant moves' : `${from.restaurants} restaurants move`} into the ISO’s portfolio from now, at the rates below.`
              : 'Restaurants they bring in from now on are the ISO’s.'}
            {' '}Commissions they already earned as an agent stay theirs.
          </Notice>
        )}
        {!editing && !from && (
          <Typography variant="body2" sx={{ color: T.text2, mb: 2.5 }}>
            An Independent Sales Organization: a company, or one person, with its own sales agents. The ISO gets an email with a temporary password, signs in on this site to its ISO portal, and adds its own agents there.
          </Typography>
        )}

        <Typography component="h3" sx={{ ...section, mt: 0 }}>Company</Typography>
        <Typography sx={sectionSub}>Usually a company, but it can be one person.</Typography>
        <Grid container columnSpacing={1.5} rowSpacing={1.75}>
          <Grid item xs={12}>
            <FieldLabel htmlFor="iso-name" label="Corporate name" />
            <TextField id="iso-name" fullWidth size="small" required value={f.corporateName} onChange={set('corporateName')} />
          </Grid>
          <Grid item xs={12} sm={7}>
            <FieldLabel htmlFor="iso-email" label="Email" />
            <TextField id="iso-email" fullWidth size="small" type="email" required disabled={editing || !!from} value={f.email} onChange={set('email')} helperText={editing ? undefined : from ? 'Their login, unchanged.' : 'Also their login.'} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <FieldLabel htmlFor="iso-phone" label="Phone" />
            <TextField id="iso-phone" fullWidth size="small" type="tel" value={f.phone} onChange={set('phone')} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <FieldLabel htmlFor="iso-address1" label="Corporate address" />
            <TextField id="iso-address1" fullWidth size="small" value={f.address1} onChange={set('address1')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel htmlFor="iso-address2" label="Suite or unit" optional />
            <TextField id="iso-address2" fullWidth size="small" value={f.address2} onChange={set('address2')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel htmlFor="iso-city" label="City" />
            <TextField id="iso-city" fullWidth size="small" value={f.city} onChange={set('city')} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FieldLabel htmlFor="iso-state" label="State" />
            <TextField id="iso-state" fullWidth size="small" value={f.state} onChange={set('state')} inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FieldLabel htmlFor="iso-zip" label="ZIP" />
            <TextField id="iso-zip" fullWidth size="small" value={f.zip} onChange={set('zip')} inputProps={{ inputMode: 'numeric' }} />
          </Grid>
        </Grid>

        <Typography component="h3" sx={section}>Tax ID</Typography>
        <Typography sx={sectionSub}>The company’s EIN, or the person’s SSN. Encrypted and stored securely.</Typography>
        <Grid container columnSpacing={1.5} rowSpacing={1.75}>
          <Grid item xs={12} sm={4}>
            <FieldLabel htmlFor="iso-tax-type" label="Type" />
            <TextField id="iso-tax-type" select fullWidth size="small" value={f.taxIdType} onChange={set('taxIdType')}>
              <MenuItem value="EIN">EIN (company)</MenuItem>
              <MenuItem value="SSN">SSN (person)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={8}>
            <FieldLabel htmlFor="iso-tax-id" label={f.taxIdType === 'SSN' ? 'SSN' : 'EIN'} />
            <TextField id="iso-tax-id" fullWidth size="small" value={f.taxId} onChange={set('taxId')} inputProps={{ autoComplete: 'off', inputMode: 'numeric' }} />
          </Grid>
        </Grid>

        <Typography component="h3" sx={section}>Bank</Typography>
        <Typography sx={sectionSub}>Where RushMyFood deposits the ISO’s earnings. Encrypted and stored securely.</Typography>
        <BankFields value={bank} onChange={setBank} idPrefix="iso-bank" />

        <Typography component="h3" sx={section}>What the ISO earns</Typography>
        <Typography sx={sectionSub}>
          On every order from each account the ISO or its agents bring in: a % of the subtotal (items before discounts, no tax, tips or fees), plus a flat amount per order if you like. Each account keeps the rates it was approved with.
        </Typography>
        <RatesEditor value={rates} onChange={setRates} />
        <Grid container columnSpacing={1.5} sx={{ mt: 1.5 }}>
          <Grid item xs={12} sm={6}>
            <FieldLabel htmlFor="iso-extra-share" label="Above the minimums" />
            <TextField
              id="iso-extra-share" fullWidth size="small" type="number" value={f.extraShare} onChange={set('extraShare')}
              inputProps={{ min: 0, max: 100, step: 5 }}
              InputProps={{ endAdornment: <InputAdornment position="end">% of the extra</InputAdornment> }}
              helperText="Its share of an account's fees above the minimums (e.g. delivery at 30% vs the 20% minimum). Its agents get their split of it."
            />
          </Grid>
        </Grid>

        <Typography component="h3" sx={section}>Who RushMyFood pays</Typography>
        <Typography sx={sectionSub}>The ISO decides, and can change it in its portal. Agents’ shares and bonuses always come out of the ISO’s earnings.</Typography>
        <RadioGroup value={f.payoutTo} onChange={set('payoutTo')}>
          <FormControlLabel value="ISO" control={<Radio />} label="The ISO gets everything and pays its agents itself" />
          <FormControlLabel value="AGENTS" control={<Radio />} label="RushMyFood pays each agent their share directly, and the ISO the rest" />
        </RadioGroup>

        {editing && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              sx={{ '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: 14 } }}
              control={<Switch checked={f.isActive} onChange={e => setF(x => ({ ...x, isActive: e.target.checked }))} />}
              label={f.isActive ? 'Active' : 'Deactivated: the ISO can’t sign in, and its agents’ links stop working'}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <GhostButton onClick={onClose} disabled={busy}>Cancel</GhostButton>
        <PrimaryButton onClick={save} busy={busy} disabled={!f.corporateName || (!editing && !f.email)}>
          {editing ? 'Save' : from ? 'Convert to ISO' : 'Create ISO'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  )
}
